import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Phone, PhoneOutgoing, Copy } from "lucide-react";
import { toast } from "sonner";
import { placeCall, fetchWhatsappMessage } from "@/lib/api";
import { isCallableNumber, isFullPhoneNumber, e164Digits, e164Plus, copyText, DEFAULT_WHATSAPP_MESSAGES } from "@/lib/call";
import { LINE_LABELS, LINE_NATIONAL, lineLabel, getLastLine, setLastLine } from "@/lib/lines";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Shared click-to-call flow: the server rings the member's own phone first,
// then the customer. Button disabled for 10 s after a launched call.
export const usePlaceCall = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [pending, setPending] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const launch = async (number, displayName, line) => {
    setPending(true);
    try {
      const res = await placeCall(number, line);
      const usedLine = res?.line || line;
      const lineTxt = usedLine && lineLabel(usedLine)
        ? ` — appel depuis ${lineLabel(usedLine)} (${LINE_NATIONAL[usedLine]})`
        : "";
      toast.success(`Votre téléphone va sonner${lineTxt}`, {
        description: `Décrochez pour appeler ${displayName || number}.`,
      });
      if (usedLine) setLastLine(user?.id, usedLine);
      setCooldown(true);
      setTimeout(() => setCooldown(false), 10000);
      // The outgoing call clears the "À rappeler" state: refresh the lists so
      // the badge disappears as soon as the call shows up in the history.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["calls-grouped"] });
        qc.invalidateQueries({ queryKey: ["calls"] });
        qc.invalidateQueries({ queryKey: ["contact"] });
      }, 4000);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Impossible de lancer l'appel", {
        description: typeof detail === "string" ? detail : "Réessayez dans un instant.",
      });
      return false;
    } finally {
      setPending(false);
    }
  };

  return { launch, pending, cooldown };
};

// Confirmation sheet shown before every call. When onLineChange is provided,
// the outgoing line is selectable; when only `line` is given, it's shown as
// fixed info (callbacks always use the line the client called).
export const ConfirmCallDialog = ({ open, onOpenChange, number, name, onLaunch, pending, line, onLineChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[360px]" data-testid="call-confirm-sheet">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <span className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <PhoneOutgoing className="h-4 w-4" />
          </span>
          Lancer un appel
        </DialogTitle>
        <DialogDescription className="sr-only">Confirmation avant appel sortant</DialogDescription>
      </DialogHeader>
      <p className="text-sm text-slate-600">
        Votre téléphone va sonner. Décrochez pour appeler{" "}
        <span className="font-semibold text-slate-900">{name || number}</span>.
      </p>
      {onLineChange ? (
        <div data-testid="call-line-selector" className="space-y-1.5">
          <p className="text-xs tracking-[0.06em] uppercase font-semibold text-slate-400">Ligne d'appel</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(LINE_LABELS).map((lid) => (
              <button
                key={lid}
                type="button"
                data-testid={`call-line-${lid}`}
                onClick={() => onLineChange(lid)}
                className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                  line === lid
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <span className="block text-sm font-semibold leading-tight">{LINE_LABELS[lid]}</span>
                <span className="block text-[11px] text-slate-400 tabular-nums">{LINE_NATIONAL[lid]}</span>
              </button>
            ))}
          </div>
        </div>
      ) : line && lineLabel(line) ? (
        <p data-testid="call-line-fixed" className="text-xs text-slate-500">
          Appel depuis <span className="font-semibold text-slate-700">{lineLabel(line)}</span>{" "}
          <span className="tabular-nums">({LINE_NATIONAL[line]})</span>
        </p>
      ) : null}
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          data-testid="call-confirm-cancel"
          onClick={() => onOpenChange(false)}
          className="flex-1 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2.5 hover:bg-slate-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="button"
          data-testid="call-confirm-launch"
          onClick={onLaunch}
          disabled={pending}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          <Phone className="h-4 w-4" /> {pending ? "Appel…" : "Lancer l'appel"}
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

// "Appeler" button for a known number (number view / contact). Disabled with a
// hint when the number is not a French landline/mobile/09. The outgoing line
// is selectable; default = last line used by this member.
export const CallButton = ({ numberKey, number, name, defaultLine }) => {
  const { user } = useAuth();
  const callable = isCallableNumber(numberKey || number);
  const { launch, pending, cooldown } = usePlaceCall();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [line, setLine] = useState(
    () => getLastLine(user?.id) || defaultLine || "reco_pneus"
  );

  if (!callable) {
    return (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          data-testid="call-button-disabled"
          disabled
          className="inline-flex items-center gap-2 rounded-lg bg-slate-200 text-slate-400 text-sm font-semibold px-4 py-2.5 cursor-not-allowed"
        >
          <Phone className="h-4 w-4" /> Appeler
        </button>
        <p data-testid="call-not-allowed-hint" className="text-xs text-slate-400">
          Numéro non autorisé pour les appels sortants
        </p>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        data-testid="call-button"
        onClick={() => setConfirmOpen(true)}
        disabled={pending || cooldown}
        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-50 transition-all"
      >
        <Phone className="h-4 w-4" /> Appeler
      </button>
      <ConfirmCallDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        number={number}
        name={name}
        pending={pending}
        line={line}
        onLineChange={setLine}
        onLaunch={async () => {
          const ok = await launch(number, name, line);
          if (ok) setConfirmOpen(false);
        }}
      />
    </>
  );
};


// Compact "Rappeler" button shown on "À rappeler" rows/cards in the calls
// list. Calls back FROM THE SAME LINE the client used (fixed, shown in the
// confirmation). Never triggers the row's onSelect.
export const CallbackButton = ({ numberKey, number, name, line, testid }) => {
  const { launch, pending, cooldown } = usePlaceCall();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isCallableNumber(numberKey || number)) return null;

  return (
    <>
      <button
        type="button"
        data-testid={testid || `callback-btn-${numberKey}`}
        onClick={(e) => {
          e.stopPropagation();
          setConfirmOpen(true);
        }}
        disabled={pending || cooldown}
        className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white text-red-700 text-xs font-semibold px-2.5 py-0.5 hover:bg-red-50 active:bg-red-100 disabled:opacity-50 transition-colors whitespace-nowrap"
      >
        <PhoneOutgoing className="h-3 w-3" /> Rappeler
      </button>
      <ConfirmCallDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        number={number}
        name={name}
        pending={pending}
        line={line}
        onLaunch={async () => {
          const ok = await launch(number, name, line);
          if (ok) setConfirmOpen(false);
        }}
      />
    </>
  );
};


// Official WhatsApp glyph (Simple Icons, CC0) in brand green.
export const WhatsAppIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="#25D366" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

// "WhatsApp": opens the WhatsApp conversation (wa.me) with this number and a
// pre-filled message (admin-editable in Réglages). Only pre-filled — the user
// can still edit it in WhatsApp; nothing is ever sent automatically.
// Hidden for short numbers / internal extensions that can't be mobiles.
// compact=true renders the small pill used inside expanded call details.
export const WhatsAppButton = ({ number, line, compact = false, testid = "whatsapp-btn" }) => {
  const effectiveLine = LINE_LABELS[line] ? line : "reco_pneus";
  const { data } = useQuery({
    queryKey: ["whatsapp-message", effectiveLine],
    queryFn: () => fetchWhatsappMessage(effectiveLine),
    staleTime: 5 * 60 * 1000,
  });
  if (!isFullPhoneNumber(number)) return null;
  const message = data?.message || DEFAULT_WHATSAPP_MESSAGES[effectiveLine];
  const href = `https://wa.me/${e164Digits(number)}?text=${encodeURIComponent(message)}`;
  const cls = compact
    ? "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-[#25D366] hover:text-[#128C7E] active:bg-emerald-50 transition-colors whitespace-nowrap"
    : "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2.5 hover:border-[#25D366] hover:text-[#128C7E] transition-colors";
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={cls}
    >
      <WhatsAppIcon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} /> WhatsApp
    </a>
  );
};

// "Copier": copies the number as E.164 with "+" (e.g. +33612345678).
export const CopyNumberButton = ({ number, compact = false, testid = "copy-number-btn" }) => {
  const e164 = e164Plus(number);
  if (!e164) return null;
  const copy = async (e) => {
    e.stopPropagation();
    const ok = await copyText(e164);
    if (ok) toast.success("Numéro copié");
    else toast.error("Impossible de copier le numéro");
  };
  const cls = compact
    ? "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors whitespace-nowrap"
    : "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2.5 hover:border-emerald-300 hover:text-emerald-700 transition-colors";
  return (
    <button type="button" data-testid={testid} onClick={copy} className={cls}>
      <Copy className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} /> Copier
    </button>
  );
};